import React from 'react';

type ButtonVariant = 'primary' | 'secondary' | 'outline' | 'ghost' | 'light' | 'danger' | 'dark';
type ButtonSize = 'sm' | 'md' | 'lg';

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  fullWidth?: boolean;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  children: React.ReactNode;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(({
  variant = 'primary',
  size = 'md',
  fullWidth = false,
  leftIcon,
  rightIcon,
  children,
  className = '',
  ...props
}, ref) => {
  // Base styles - removed focus:ring and added focus:outline-none
  const baseStyles = 'font-medium rounded-md focus:outline-none transition-colors duration-200 inline-flex items-center justify-center gap-2 whitespace-nowrap';

  // Variant styles
  const variantStyles: Record<ButtonVariant, string> = {
    primary: 'bg-blue-600 text-white hover:bg-blue-700',
    secondary: 'bg-gray-600 text-white hover:bg-gray-700',
    outline: 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50',
    ghost: 'bg-gray-100 text-gray-700 hover:bg-gray-200',
    light: 'bg-gray-100 text-gray-600 hover:bg-gray-200',
    dark: 'bg-gray-900 text-white hover:bg-gray-800',
    danger: 'bg-red-600 text-white hover:bg-red-700',
  };

  // Size styles
  const sizeStyles: Record<ButtonSize, string> = {
    sm: 'px-3 py-1.5 text-xs',
    md: 'px-4 py-2 text-sm',
    lg: 'px-5 py-2.5 text-sm',
  };

  // Width styles
  const widthStyles = fullWidth ? 'w-full' : '';

  // Disabled styles
  const disabledStyles = props.disabled ? 'opacity-50 cursor-not-allowed' : '';

  // Combine all styles
  const buttonClasses = `
    ${baseStyles}
    ${variantStyles[variant]}
    ${sizeStyles[size]}
    ${widthStyles}
    ${disabledStyles}
    ${className}
  `.trim().replace(/\s+/g, ' ');

  return (
    <button ref={ref} className={buttonClasses} {...props}>
      {leftIcon && <span className="inline-flex items-center w-4 h-4">{leftIcon}</span>}
      <span>{children}</span>
      {rightIcon && <span className="inline-flex items-center w-4 h-4">{rightIcon}</span>}
    </button>
  );
});

Button.displayName = 'Button';

export default Button;
